import React, { forwardRef } from "react";
import PropTypes from "prop-types";
import {
  Drawer,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerBody,
  DrawerFooter,
  makeStyles,
  TabList,
  Tab,
  mergeClasses,
  Divider
} from "@fluentui/react-components";
import { CloseButton } from "../input/CloseButton";
import { useResponsiveDrawerPosition } from "../drawer/responsive-drawer-utils";

// Define custom styles using Fluent UI's makeStyles
const useStyles = makeStyles({
  sidebar: {
    width: "100%",
    "@media (min-width: 576px)": {
      width: "400px"
    },
    display: "flex",
    flexDirection: "column",
    height: "100%" // Ensure the sidebar takes full height
  },
  header: {
    backgroundColor: "var(--primaryColorBackground)"
  },
  titleContainer: {
    display: "flex",
    alignItems: "center"
  },
  beforeTitle: {
    marginRight: "8px"
  },
  afterTitle: {
    marginLeft: "8px"
  },
  customTitle: {
    flexGrow: 1
  },
  tabsContainer: {
    padding: "0 24px 8px",
    borderBottom: "1px solid var(--neutralStroke1)",
    overflow: "hidden",
    width: "100%",
    "& .fui-Tab": {
      marginLeft: "-12px"
    }
  },
  tabList: {
    overflow: "auto",
    scrollbarWidth: "thin"
  },
  content: {
    flexGrow: 1,
    overflowY: "auto",
    padding: "24px"
  },
  footer: {
    padding: "24px",
    display: "flex",
    justifyContent: "flex-end",
    borderTop: "1px solid var(--neutralStroke1)"
  }
});

export const Sidebar = forwardRef(function Sidebar(
  {
    title,
    afterTitle,
    children,
    contentClassName,
    className,
    headerClassName,
    tabs,
    currentTab,
    changeTab,
    customTitle,
    footer,
    footerClassName,
    onClose,
    position = "start",
    mobilePosition = "bottom",
    responsivePosition = true,
    open = true,
    as = "aside",
    ...rest
  },
  ref // Receive the forwarded ref
) {
  const styles = useStyles();
  const responsiveDrawerPosition = useResponsiveDrawerPosition({ desktopPosition: position, mobilePosition });
  const drawerPosition = responsivePosition ? responsiveDrawerPosition : position;

  return (
    <Drawer
      className={mergeClasses(styles.sidebar, className)}
      open={open}
      onOpenChange={onClose}
      modalType="non-modal"
      position={drawerPosition}
      as={as}
      {...rest}
    >
      <DrawerHeader className={mergeClasses(styles.header, headerClassName)}>
        {title && <DrawerHeaderTitle action={onClose && <CloseButton onClick={onClose} />}>{title}</DrawerHeaderTitle>}
        {customTitle && <div className={styles.customTitle}>{customTitle}</div>}
        {afterTitle && <div className={styles.afterTitle}>{afterTitle}</div>}
      </DrawerHeader>

      {tabs && tabs.length > 0 && (
        <div className={styles.tabsContainer}>
          <TabList
            selectedValue={currentTab}
            onTabSelect={(e, data) => changeTab(data.value)}
            className={styles.tabList}
          >
            {tabs.map(tab => (
              <Tab key={tab.value} value={tab.value}>
                {tab.label}
              </Tab>
            ))}
          </TabList>
          <Divider />
        </div>
      )}

      {/* Content */}
      <DrawerBody ref={ref} className={mergeClasses(styles.content, contentClassName)}>
        {children}
      </DrawerBody>

      {/* Footer */}
      {footer && <DrawerFooter className={mergeClasses(styles.footer, footerClassName)}>{footer}</DrawerFooter>}
    </Drawer>
  );
});

Sidebar.propTypes = {
  title: PropTypes.node,
  beforeTitle: PropTypes.node,
  afterTitle: PropTypes.node,
  customTitle: PropTypes.node,
  children: PropTypes.node,
  className: PropTypes.string,
  contentClassName: PropTypes.string,
  disableOverflowScroll: PropTypes.bool,
  headerClassName: PropTypes.string,
  headerContainer: PropTypes.string,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired
    })
  ),
  currentTab: PropTypes.string,
  changeTab: PropTypes.func,
  footer: PropTypes.node,
  onClose: PropTypes.func,
  position: PropTypes.oneOf(["start", "end", "bottom"]),
  footerClassName: PropTypes.string,
  open: PropTypes.bool,
  mobilePosition: PropTypes.oneOf(["start", "end", "bottom"]),
  responsivePosition: PropTypes.bool,
  as: PropTypes.string
};

Sidebar.defaultProps = {
  disableOverflowScroll: false,
  open: true
};
